import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  getSyncConfig,
  saveSyncConfig,
  isSyncConfigured,
  recordChange,
  fullSync,
} from './syncService.js';

const SyncContext = createContext(null);

function SyncProvider({ children }) {
  const [syncStatus, setSyncStatus] = useState('idle'); // 'idle' | 'syncing' | 'offline' | 'error'
  const [lastSynced, setLastSynced] = useState(null);
  const [config, setConfig] = useState(null);
  const [configured, setConfigured] = useState(false);
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const debounceTimerRef = useRef(null);

  // Load config on mount
  useEffect(() => {
    async function load() {
      const cfg = await getSyncConfig();
      setConfig(cfg || null);
      setConfigured(Boolean(cfg?.apiEndpoint));
    }
    load();
  }, []);

  const triggerSync = useCallback(async () => {
    if (!configured || !navigator.onLine) return;

    const cfg = await getSyncConfig();
    if (!cfg?.apiEndpoint) return;

    setSyncStatus('syncing');
    try {
      await fullSync(cfg.apiEndpoint);
      setLastSynced(new Date());
      setSyncStatus('idle');
      window.dispatchEvent(new CustomEvent('sync-completed'));
    } catch (err) {
      console.error('Sync failed:', err);
      if (!navigator.onLine) {
        setSyncStatus('offline');
      } else {
        setSyncStatus('error');
      }
    }
  }, [configured]);

  // Monitor online/offline
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (syncStatus === 'offline') {
        setSyncStatus('idle');
      }
      // Trigger sync when coming back online
      if (configured) {
        triggerSync();
      }
    };
    const handleOffline = () => {
      setIsOnline(false);
      setSyncStatus('offline');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    if (!navigator.onLine) {
      setIsOnline(false);
      setSyncStatus('offline');
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [configured, syncStatus, triggerSync]);

  // Listen for sync-change-recorded events and debounce sync triggers
  useEffect(() => {
    const handleChange = () => {
      if (!configured || !navigator.onLine) return;
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        triggerSync();
      }, 3000);
    };

    window.addEventListener('sync-change-recorded', handleChange);
    return () => {
      window.removeEventListener('sync-change-recorded', handleChange);
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [configured, triggerSync]);

  const updateConfig = useCallback(async (newConfig) => {
    await saveSyncConfig(newConfig);
    setConfig(newConfig);
    setConfigured(Boolean(newConfig?.apiEndpoint));
  }, []);

  const trackChange = useCallback(async (table, recordId, operation) => {
    await recordChange(table, recordId, operation);
  }, []);

  const forceSync = useCallback(async () => {
    if (!configured) {
      console.log('[SyncProvider] forceSync skipped — not configured');
      return;
    }
    const cfg = await getSyncConfig();
    if (!cfg?.apiEndpoint) {
      console.log('[SyncProvider] forceSync skipped — no apiEndpoint in config');
      return;
    }

    console.log('[SyncProvider] forceSync starting with endpoint:', cfg.apiEndpoint);
    setSyncStatus('syncing');
    try {
      await fullSync(cfg.apiEndpoint);
      setLastSynced(new Date());
      setSyncStatus('idle');
      window.dispatchEvent(new CustomEvent('sync-completed'));
      console.log('[SyncProvider] forceSync completed successfully');
    } catch (err) {
      console.error('Force sync failed:', err);
      if (!navigator.onLine) {
        setSyncStatus('offline');
      } else {
        setSyncStatus('error');
      }
    }
  }, [configured]);

  const value = {
    syncStatus,
    lastSynced,
    config,
    configured,
    isOnline,
    updateConfig,
    trackChange,
    triggerSync,
    forceSync,
  };

  return React.createElement(SyncContext.Provider, { value }, children);
}

export { SyncContext, SyncProvider };
